<template>
  <div
    ref="panelRef"
    class="winter-service-map-panel"
  >
    <map-base
      ref="mapBaseRef"
      :show-geo-search-control="false"
      class="winter-service-map-panel__map"
    />
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import L from 'leaflet'
import MapBase from '@/global/components/map/MapBase.vue'

const MARKER_COLOR = '#65AD45'
const MARKER_ICON_KEY = 'winterServiceLocation'
const SINGLE_MARKER_ZOOM = 15

const props = defineProps({
  latitude: {
    type: [Number, String],
    default: null
  },
  longitude: {
    type: [Number, String],
    default: null
  }
})

const mapBaseRef = ref(null)
const panelRef = ref(null)
let isMapReady = false
let resizeObserver = null

// Same pin shape the other winter service / temporary regulation maps draw. The
// anchor is deliberately not the icon box's bottom-center: the `.custom-marker`
// span's own negative margin puts its centre at (10, 10) and the -45deg rotation
// turns its unrounded bottom-left corner into the pin's tip at (10, 31).
const markerIcon = L.divIcon({
  className: 'marker-icon',
  iconSize: [30, 30],
  iconAnchor: [10, 31],
  html: `<span style="background-color: ${MARKER_COLOR}" class="custom-marker"></span>`
})

function hasCoordinates () {
  return props.latitude !== null && props.latitude !== '' &&
    props.longitude !== null && props.longitude !== ''
}

function renderMarker () {
  if (!isMapReady || !mapBaseRef.value) {
    return
  }

  if (!hasCoordinates()) {
    mapBaseRef.value.removeMarkers()
    return
  }

  mapBaseRef.value.generateMarkers({
    markers: [
      {
        lat: Number(props.latitude),
        lon: Number(props.longitude),
        icon: MARKER_ICON_KEY
      }
    ],
    icons: {
      [MARKER_ICON_KEY]: markerIcon
    },
    // A single base is one point, so there are no bounds worth fitting - clustering
    // it would only hide it behind a "1" bubble.
    useClustering: false,
    fitMarkers: false
  })

  mapBaseRef.value.getMapObject()?.setView(
    [Number(props.latitude), Number(props.longitude)],
    SINGLE_MARKER_ZOOM
  )
}

onMounted(() => {
  // Registering through `onMapReady` rather than the `ready` event skips
  // MapBase's own default company/Zagreb re-centering, which would otherwise
  // run right after and override the view set above.
  mapBaseRef.value?.onMapReady(() => {
    isMapReady = true
    renderMarker()
  })

  // The panel is laid out against whatever sits next to it, so its height moves
  // with that - a textarea growing, a validation message appearing. Leaflet only
  // learns about a resized container when it is told, and until it is it keeps
  // painting tiles for the size it was first laid out at.
  if (typeof ResizeObserver === 'undefined' || !panelRef.value) {
    return
  }

  resizeObserver = new ResizeObserver(() => mapBaseRef.value?.invalidateSize?.())
  resizeObserver.observe(panelRef.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})

watch(() => [props.latitude, props.longitude], renderMarker)
</script>

<style scoped>
.winter-service-map-panel {
  display: flex;
  height: 100%;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  /* Leaflet's own controls/panes reach z-index 1000-9999 internally. Without its
     own stacking context that competes with page-level chrome like the sticky app
     header once the page scrolls - `isolation: isolate` caps all of this map's
     descendants to stack only against each other. */
  isolation: isolate;
}

.winter-service-map-panel__map {
  flex: 1 1 auto;
}
</style>

<style lang="scss">
/* Unscoped: these divIcons are appended by Leaflet outside this component's
   scoped DOM tree, so a scoped rule here would never reach them. */
.marker-icon {
  z-index: 201 !important;
}

.custom-marker {
  width: 30px;
  height: 30px;
  border-radius: 50% 50% 50% 0;
  position: absolute;
  transform: rotate(-45deg);
  left: 50%;
  top: 50%;
  margin: -20px 0 0 -20px;
}

.custom-marker:after {
  content: "";
  width: 8px;
  height: 8px;
  margin: 11px 0 0 11px;
  background: #e6e6e6;
  position: absolute;
  border-radius: 50%;
}
</style>
